/*
 * R1 Kit Topic Picker — bundle product configurator.
 *
 * Vanilla, no dependencies. Instance-safe: every [data-r1-kit-picker] section
 * wires up its own state, scoped by the section root.
 *
 * Business model: the bundle price is ALREADY baked into the product. There is
 * NO discount / price math here. The customer PICKS which topics they want
 * (target count N); we add THIS ONE product's selected variant to cart at qty 1
 * with the chosen topics recorded as PUBLIC line-item properties so they show
 * at checkout and on the order.
 *
 * State model:
 *   filter  : active domain chip ('all' or a domain string)
 *   selected: [{ handle, name, domain, lang: 'EN'|'ES', qty }]
 *   Total picked = sum of qty (a topic is repeatable across languages / counts,
 *   so quantity>1 and EN+ES of the same topic are allowed), and must equal N
 *   before add-to-cart enables.
 *
 * Target count N — resolved in this order (PHASED, future-proofed):
 *   (a) a variant option whose name matches a configured Size/Bundle Size name
 *       -> the SELECTED variant's numeric size (re-resolved on variant change);
 *   (b) product.metafields.r1_meta.kit_pick_count (data-meta-count) — drives it
 *       today (all bundle products are single-variant);
 *   (c) parse the leading / deck number from the product title (last resort).
 */
(function () {
  function initSection(root) {
    if (root.dataset.r1KitPickerReady === 'true') return;
    root.dataset.r1KitPickerReady = 'true';

    // ── Element refs ─────────────────────────────────────────────────────────
    var hintEl = root.querySelector('[data-r1-picker-hint]');
    var chipEls = qsa(root, '[data-r1-picker-chip]');
    var topicEls = qsa(root, '[data-r1-picker-topic]');

    var countEl = root.querySelector('[data-r1-picker-count]');
    var sizeEl = root.querySelector('[data-r1-picker-size]');
    var progressEl = root.querySelector('[data-r1-picker-progress]');
    var linesEl = root.querySelector('[data-r1-picker-lines]');
    var emptyEl = root.querySelector('[data-r1-picker-empty]');
    var ctaEl = root.querySelector('[data-r1-picker-cta]');
    var errorEl = root.querySelector('[data-r1-picker-error]');

    // ── Config ────────────────────────────────────────────────────────────────
    var propertyPrefix = (root.getAttribute('data-property-prefix') || 'Topic').trim() || 'Topic';
    var metaCount = parseInt(root.getAttribute('data-meta-count'), 10) || 0;
    var productTitle = root.getAttribute('data-title') || '';
    // Full-word language toggle labels (editable in the theme editor). We keep
    // the data-lang values 'EN'/'ES' and the cart-property format untouched —
    // only the visible button text and its accessible naming come from here.
    var langEnLabel = root.getAttribute('data-lang-en-label') || 'English';
    var langEsLabel = root.getAttribute('data-lang-es-label') || 'Español';
    var langAriaLabel = root.getAttribute('data-lang-aria-label') || 'Card deck language';
    var langEnOnlyNote = root.getAttribute('data-lang-en-only') || 'English only';
    var sizeOptionNames = (root.getAttribute('data-size-option-names') || '')
      .split(',')
      .map(function (s) { return s.trim().toLowerCase(); })
      .filter(Boolean);

    // Variant + option data (for source (a) and add-to-cart).
    var variants = readJSON(root, '[data-r1-picker-variants]', []);
    var optionNames = readJSON(root, '[data-r1-picker-option-names]', []);
    // Index of the option that looks like a Size / Bundle Size option, if any.
    var sizeOptionIndex = -1;
    optionNames.forEach(function (name, i) {
      if (sizeOptionIndex === -1 && sizeOptionNames.indexOf(String(name).trim().toLowerCase()) >= 0) {
        sizeOptionIndex = i;
      }
    });

    // ── Parse per-topic data ────────────────────────────────────────────────
    var topics = {};
    topicEls.forEach(function (el) {
      var handle = el.getAttribute('data-handle');
      topics[handle] = {
        el: el,
        handle: handle,
        name: el.getAttribute('data-name') || '',
        domain: el.getAttribute('data-domain') || '',
        // Only topics with a Spanish deck offer the Español toggle (Liquid
        // emits data-has-es from the section's "Topics available in Español"
        // setting). Everything else is English only.
        hasEs: el.getAttribute('data-has-es') === 'true',
      };
    });

    // ── State ───────────────────────────────────────────────────────────────
    var state = {
      variantId: parseInt(root.getAttribute('data-variant-id'), 10) || 0,
      filter: 'all',
      selected: [],
    };

    // ── Target count resolution (a) → (b) → (c) ───────────────────────────────
    // Parse the first integer we can trust from the title. Group/Topic bundles
    // lead with the count ("12 Group Kits Bundle (12-decks)" → 12); practitioner
    // kits carry it as "… — N decks" (no leading number). Prefer a LEADING
    // number; fall back to a "N decks"/"N Topics" marker.
    function parseTitleCount(title) {
      var lead = title.match(/^\s*(\d+)\b/);
      if (lead) return parseInt(lead[1], 10);
      var decks = title.match(/[—\-]\s*(\d+)\s*(?:decks?|topics?)/i);
      if (decks) return parseInt(decks[1], 10);
      return 0;
    }

    // Numeric size from the selected variant's size option, if present.
    function sizeFromVariant(variantId) {
      if (sizeOptionIndex < 0 || !variantId) return 0;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId)) {
          var opts = variants[i].options || [];
          var raw = opts[sizeOptionIndex];
          if (raw == null) return 0;
          var m = String(raw).match(/\d+/);
          return m ? parseInt(m[0], 10) : 0;
        }
      }
      return 0;
    }

    function resolveTargetCount() {
      var fromVariant = sizeFromVariant(state.variantId); // (a)
      if (fromVariant > 0) return fromVariant;
      if (metaCount > 0) return metaCount; // (b)
      return parseTitleCount(productTitle); // (c)
    }

    // ── Helpers ───────────────────────────────────────────────────────────────
    function totalQty() {
      return state.selected.reduce(function (a, s) { return a + s.qty; }, 0);
    }

    // ── Mutations ─────────────────────────────────────────────────────────────
    function toggleTopic(handle) {
      var i = findIndex(handle);
      if (i >= 0) {
        state.selected.splice(i, 1);
      } else if (totalQty() < currentSize()) {
        var t = topics[handle];
        if (!t) return;
        state.selected.push({ handle: handle, name: t.name, domain: t.domain, hasEs: t.hasEs, lang: 'EN', qty: 1 });
      }
      render();
    }

    function removeTopic(handle) {
      var i = findIndex(handle);
      if (i >= 0) state.selected.splice(i, 1);
      render();
    }

    function setLang(handle, lang) {
      state.selected.forEach(function (s) {
        if (s.handle !== handle) return;
        if (lang === 'ES' && !s.hasEs) return; // no Spanish deck for this topic
        s.lang = lang;
      });
      render();
    }

    function inc(handle) {
      if (totalQty() >= currentSize()) return;
      state.selected.forEach(function (s) { if (s.handle === handle) s.qty += 1; });
      render();
    }

    function dec(handle) {
      state.selected.forEach(function (s) { if (s.handle === handle) s.qty -= 1; });
      state.selected = state.selected.filter(function (s) { return s.qty > 0; });
      render();
    }

    function findIndex(handle) {
      return state.selected.findIndex(function (s) { return s.handle === handle; });
    }

    // Cache the resolved size per render.
    var _size = 0;
    function currentSize() { return _size; }

    // ── Variant change (source (a), future-proofing) ──────────────────────────
    // React to the theme's variant-change signals AND the URL ?variant= param.
    // When N changes, trim selections so total qty never exceeds the new N.
    function setVariant(variantId) {
      if (!variantId) return;
      var idNum = parseInt(variantId, 10) || 0;
      if (!idNum || idNum === state.variantId) return;
      state.variantId = idNum;
      var n = resolveTargetCount();
      // Trim from the end so total qty never exceeds the new target.
      var over = totalQty() - n;
      for (var i = state.selected.length - 1; i >= 0 && over > 0; i--) {
        var take = Math.min(state.selected[i].qty, over);
        state.selected[i].qty -= take;
        over -= take;
      }
      state.selected = state.selected.filter(function (s) { return s.qty > 0; });
      render();
    }

    function variantFromUrl() {
      try {
        var v = new URL(window.location.href).searchParams.get('variant');
        return v ? parseInt(v, 10) : 0;
      } catch (e) { return 0; }
    }

    // ── Render ────────────────────────────────────────────────────────────────
    function render() {
      _size = resolveTargetCount();
      var count = totalQty();
      var size = _size;
      var complete = size > 0 && count === size;
      var remaining = Math.max(size - count, 0);

      // Topic tiles.
      topicEls.forEach(function (el) {
        var handle = el.getAttribute('data-handle');
        var selected = state.selected.some(function (s) { return s.handle === handle; });
        var disabled = !selected && complete;
        el.classList.toggle('is-selected', selected);
        el.classList.toggle('is-disabled', disabled);
        var matchesDomain = state.filter === 'all' || el.getAttribute('data-domain') === state.filter;
        el.classList.toggle('is-hidden', !matchesDomain);
      });

      // Chips.
      chipEls.forEach(function (c) {
        c.classList.toggle('is-active', (c.getAttribute('data-domain') || 'all') === state.filter);
      });

      // Hint line.
      if (hintEl) {
        if (complete) {
          hintEl.textContent = hintEl.getAttribute('data-full') || '';
        } else if (remaining === 1) {
          hintEl.textContent = (hintEl.getAttribute('data-remaining-one') || '').replace('[n]', remaining);
        } else {
          hintEl.textContent = (hintEl.getAttribute('data-remaining-many') || '').replace('[n]', remaining);
        }
      }

      // Summary head.
      if (countEl) countEl.textContent = count;
      if (sizeEl) sizeEl.textContent = size;
      if (progressEl) progressEl.style.width = (size > 0 ? Math.round((count / size) * 100) : 0) + '%';

      renderLines(count, size);

      if (ctaEl) {
        ctaEl.disabled = !complete;
        if (complete) {
          ctaEl.textContent = ctaEl.getAttribute('data-label-full') || 'Add bundle to cart';
        } else {
          ctaEl.textContent = (ctaEl.getAttribute('data-label-incomplete') || '').replace('[n]', remaining);
        }
      }
    }

    function renderLines(count, size) {
      qsa(linesEl, '.r1-picker__line').forEach(function (n) { n.remove(); });
      if (emptyEl) emptyEl.style.display = state.selected.length ? 'none' : '';

      var canInc = count < size;

      state.selected.forEach(function (sel) {
        var row = document.createElement('div');
        row.className = 'r1-picker__line';
        row.innerHTML =
          '<div class="r1-picker__line-top">' +
            '<span class="r1-picker__line-name"></span>' +
            '<button type="button" class="r1-picker__line-remove" aria-label="Remove">×</button>' +
          '</div>' +
          '<div class="r1-picker__line-controls">' +
            '<div class="r1-picker__lang" role="group">' +
              '<button type="button" class="r1-picker__lang-btn" data-lang="EN"></button>' +
              '<button type="button" class="r1-picker__lang-btn" data-lang="ES"></button>' +
            '</div>' +
            '<div class="r1-picker__qty">' +
              '<button type="button" class="r1-picker__qty-btn r1-picker__qty-btn--dec" aria-label="Decrease">−</button>' +
              '<span class="r1-picker__qty-val"></span>' +
              '<button type="button" class="r1-picker__qty-btn r1-picker__qty-btn--inc" aria-label="Increase">+</button>' +
            '</div>' +
          '</div>';

        row.querySelector('.r1-picker__line-name').textContent = sel.name;
        row.querySelector('.r1-picker__qty-val').textContent = sel.qty;

        // Accessible group naming (set via property to avoid HTML escaping).
        row.querySelector('.r1-picker__lang').setAttribute('aria-label', langAriaLabel);

        var enBtn = row.querySelector('[data-lang="EN"]');
        var esBtn = row.querySelector('[data-lang="ES"]');
        // Full-word labels (textContent = safe, no escaping needed).
        enBtn.textContent = langEnLabel;
        esBtn.textContent = langEsLabel;
        enBtn.classList.toggle('is-active', sel.lang === 'EN');
        esBtn.classList.toggle('is-active', sel.lang === 'ES');
        enBtn.setAttribute('aria-pressed', sel.lang === 'EN' ? 'true' : 'false');
        esBtn.setAttribute('aria-pressed', sel.lang === 'ES' ? 'true' : 'false');

        // Topics without a Spanish deck: Español stays visible but inert, with
        // an accessible reason. aria-disabled (not disabled) so the tooltip
        // still shows; setLang() also guards, so clicks are harmless.
        if (!sel.hasEs) {
          esBtn.setAttribute('aria-disabled', 'true');
          esBtn.title = langEnOnlyNote;
        }

        var incBtn = row.querySelector('.r1-picker__qty-btn--inc');
        incBtn.disabled = !canInc;

        enBtn.addEventListener('click', function () { setLang(sel.handle, 'EN'); });
        esBtn.addEventListener('click', function () { setLang(sel.handle, 'ES'); });
        incBtn.addEventListener('click', function () { inc(sel.handle); });
        row.querySelector('.r1-picker__qty-btn--dec').addEventListener('click', function () { dec(sel.handle); });
        row.querySelector('.r1-picker__line-remove').addEventListener('click', function () { removeTopic(sel.handle); });

        linesEl.appendChild(row);
      });
    }

    // ── Build line-item properties ──────────────────────────────────────────
    // PUBLIC keys (no leading underscore) so selections show on cart/checkout/
    // order. Numbered for fulfillment clarity; a topic picked with qty>1 gets a
    // "(×N)" suffix on its line, e.g. { "Topic 1": "Grief & Loss — English (×2)" }.
    function buildProperties() {
      var props = {};
      var n = 1;
      state.selected.forEach(function (sel) {
        var lang = sel.lang === 'ES' ? 'Español' : 'English';
        var value = sel.name + ' — ' + lang;
        if (sel.qty > 1) value += ' (×' + sel.qty + ')';
        props[propertyPrefix + ' ' + n] = value;
        n += 1;
      });
      return props;
    }

    // ── Add to cart ────────────────────────────────────────────────────────
    function addToCart() {
      if (!ctaEl || ctaEl.disabled) return;
      hideError();

      if (!state.variantId) {
        showError('This product is unavailable right now. Please refresh and try again.');
        return;
      }

      var payload = {
        items: [{ id: state.variantId, quantity: 1, properties: buildProperties() }],
      };

      var original = ctaEl.textContent;
      ctaEl.disabled = true;
      ctaEl.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (d) { throw new Error(d.description || 'Add to cart failed.'); });
          return res.json();
        })
        .then(function () {
          // Let the theme refresh cart UI, then take the customer to the cart to
          // review their picked topics (consistent with r1-kit-builder).
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          window.location.href = '/cart';
        })
        .catch(function (err) {
          ctaEl.disabled = false;
          ctaEl.textContent = original;
          showError(err && err.message ? err.message : 'Something went wrong adding to cart.');
        });
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    function hideError() {
      if (!errorEl) return;
      errorEl.hidden = true;
    }

    // ── Wire events ─────────────────────────────────────────────────────────
    chipEls.forEach(function (c) {
      c.addEventListener('click', function () { state.filter = c.getAttribute('data-domain') || 'all'; render(); });
    });
    topicEls.forEach(function (el) {
      el.addEventListener('click', function () {
        if (el.classList.contains('is-disabled')) return;
        toggleTopic(el.getAttribute('data-handle'));
      });
    });
    if (ctaEl) ctaEl.addEventListener('click', addToCart);

    // Variant-change signals (future-proofing source (a)). Horizon and other
    // themes emit a variety of events; handle the common ones plus URL changes.
    ['variant:change', 'variant:changed', 'variantChange', 'product:variant-change', 'on:variant:change']
      .forEach(function (name) {
        document.addEventListener(name, function (e) {
          var d = e && e.detail ? e.detail : {};
          var v = (d.variant && d.variant.id) || d.id || d.variantId;
          if (v) setVariant(v);
        });
      });
    // Fallback: watch the ?variant= URL param (Horizon updates it on selection).
    var lastUrlVariant = variantFromUrl();
    if (lastUrlVariant) setVariant(lastUrlVariant);
    window.addEventListener('popstate', function () {
      var v = variantFromUrl();
      if (v && v !== lastUrlVariant) { lastUrlVariant = v; setVariant(v); }
    });

    render();
  }

  function readJSON(root, selector, fallback) {
    var node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return fallback;
    }
  }

  function qsa(el, sel) {
    return Array.prototype.slice.call(el.querySelectorAll(sel));
  }

  function initAll() {
    document.querySelectorAll('[data-r1-kit-picker]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-r1-kit-picker]');
    if (section) initSection(section);
  });
})();
